// https://github.com/janglad/safe-fn/blob/main/packages/safe-fn/src/result.ts#L54
// https://github.com/supermacro/neverthrow/issues/613

import { err, ok, Result, ResultAsync } from "neverthrow";

export type ActionResponse<T, E> = ActionOk<T, E> | ActionErr<T, E>;

export type ActionOk<T = undefined, E = never> = {
    ok: true;
    value: T;
};
export const actionOk = <T = undefined>(value?: T): ActionOk<T, never> => ({
    ok: true,
    value: value as T,
});

export type ActionErr<T, E> = {
    ok: false;
    error: E;
};
export const actionErr = <E>(error: E): ActionErr<never, E> => ({
    ok: false,
    error,
});

/**
 * Converts an `ActionResponse<T, E>` to a `Result<T, E>`.
 */
export const actionResponseToResult = <T, E>(
    actionResult: ActionResponse<T, E>,
): Result<T, E> => {
    if (actionResult.ok) {
        return ok(actionResult.value);
    }
    return err(actionResult.error);
};

export type InferOkData<T> = T extends Result<infer TData, any> ? TData : never;

export type InferAsyncOkData<T> =
    T extends ResultAsync<infer TData, any> ? TData : never;

export type InferErrError<T> =
    T extends Result<any, infer TError> ? TError : never;
export type InferAsyncErrError<T> =
    T extends ResultAsync<any, infer TError> ? TError : never;

export type MergeResults<T1, T2> =
    T1 extends Result<infer D1, infer E1>
        ? T2 extends Result<infer D2, infer E2>
            ? Result<D1 | D2, E1 | E2>
            : never
        : never;

export type ResultToResultAsync<T extends Result<unknown, unknown>> =
    T extends Result<infer D, infer E> ? ResultAsync<D, E> : never;

export type MergeResultAsync<T1, T2> =
    T1 extends ResultAsync<infer D1, infer E1>
        ? T2 extends ResultAsync<infer D2, infer E2>
            ? ResultAsync<D1 | D2, E1 | E2>
            : never
        : never;

export type InferActionOkData<T> =
    T extends ActionOk<infer TData, any> ? TData : never;

export type InferActionErrError<T> =
    T extends ActionErr<any, infer TError> ? TError : never;
/**
 * Converts a `ResultAsync<T, E>` to a `<ActionResponse<T, E>`.
 */
export type ResultAsyncToActionResponse<T> = [T] extends [
    ResultAsync<infer D, infer E>,
]
    ? ActionResponse<D, E>
    : never;

export type ActionResponseToResultAsync<T> = [T] extends [
    ActionResponse<infer D, infer E>,
]
    ? ResultAsync<D, E>
    : never;

export type ActionResponseToResult<T> = [T] extends [
    ActionResponse<infer D, infer E>,
]
    ? Result<D, E>
    : never;
